import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

/**
 * One suggested decision inside the "Suggested decisions" card.
 */
export interface GableSuggestionItem {
    /** e.g. "Leaky irrigation valve" */
    title: string;
    /** e.g. "Maintenance" — small blue/info pill next to the title */
    tag?: string;
    /** e.g. "Unit 112 · Maria Gonzalez · submitted Mon" */
    meta?: string;
    /** Why the AI suggests this — rendered with a sparkle icon */
    reason?: string;
    /** e.g. "Approve" | "Request info" | "Deny" */
    recommendation?: string;
    /** Pill tone for the recommendation (default 'info') */
    tone?: 'positive' | 'info' | 'caution';
    /** e.g. 96 → "96% confident" */
    confidencePct?: number;
    /** Right-side figure, e.g. "$1,480.00" or "3 units" — for money /
     *  metric answer types that aren't decisions. */
    value?: string;
    /** In-app deep link ("/app/requests/123") — makes the title clickable. */
    href?: string;
    /** Machine hint for the pill click: when present, the follow-up prompt
     *  asks the box to propose THIS action with THESE params directly —
     *  deterministic, no re-derivation. Only emitted for actions in the
     *  box's propose catalog. */
    action?: {
        action_type: string;
        params_json: string;
    };
}

/**
 * Payload for the Gable "Suggested decisions" card. The AI streams a
 * fenced JSON block; the drawer host parses it and binds it here.
 */
export interface GableSuggestionCardPayload {
    /** e.g. "Suggested decisions" */
    title: string;
    /** e.g. "5 of 14 need you" — blue pill on the right side of the header */
    badge?: string;
    /** Phosphor icon class, default 'ph-sparkle' */
    icon?: string;
    /** Small gray line under the items */
    footnote?: string;
    items: GableSuggestionItem[];
}

/**
 * Presentational card rendering AI "Suggested decisions" inside the
 * Gable chat drawer, as the same card queue the Activity tab uses: one
 * tile per item, caret opens the reason and the action in place.
 */
@Component({
    selector: 'app-gable-suggestion-card',
    templateUrl: './gable-suggestion-card.component.html',
    styleUrls: ['./gable-suggestion-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GableSuggestionCardComponent {
    @Input({ required: true }) payload!: GableSuggestionCardPayload;

    @Output() actionPrompt = new EventEmitter<string>();

    private readonly _open = new Set<number>();

    constructor(private readonly _router: Router) {}

    isOpen(index: number): boolean {
        return this._open.has(index);
    }

    hasDetail(item: GableSuggestionItem): boolean {
        return !!item.reason || item.confidencePct != null || !!item.recommendation;
    }

    toggle(index: number, item: GableSuggestionItem): void {
        if (!this.hasDetail(item)) {
            return;
        }
        if (!this._open.delete(index)) {
            this._open.add(index);
        }
    }

    /** Only an in-app path is a link; anything with a scheme or host came from the model and is dropped. */
    safeHref(item: GableSuggestionItem): string | null {
        const href = (item.href ?? '').trim();
        return href.startsWith('/app/') && !href.startsWith('/app//') ? href : null;
    }

    openRecord(event: MouseEvent, href: string): void {
        event.stopPropagation();
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }
        event.preventDefault();
        void this._router.navigateByUrl(href);
    }

    buttonVariant(item: GableSuggestionItem): string {
        return item.tone === 'positive' ? 'primary' : item.tone === 'caution' ? 'error' : 'secondary';
    }

    buttonMode(item: GableSuggestionItem): string {
        return item.tone === 'positive' || item.tone === 'caution' ? 'lighter' : 'filled';
    }

    onItemAction(item: GableSuggestionItem): void {
        const rec = item.recommendation ?? 'Proceed';
        if (item.action?.action_type) {
            // Structured fast path — the model emitted the exact action +
            // params with the suggestion, so the follow-up is deterministic.
            this.actionPrompt.emit(
                `${rec}: "${item.title}". Propose the ${item.action.action_type} action with exactly these params for my approval: ${item.action.params_json}`,
            );
            return;
        }
        this.actionPrompt.emit(`${rec}: "${item.title}". Propose this action for my approval with a preview I can confirm.`);
    }

    trackByIndex(index: number): number {
        return index;
    }
}
